import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Trash2, Check, X, Edit2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', content: '', student_type: '' });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials`, getAuthHeaders());
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/testimonials/${editingId}`, formData, getAuthHeaders());
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API}/testimonials`, formData, getAuthHeaders());
        toast.success('Testimonial added!');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', content: '', student_type: '' });
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      name: testimonial.name,
      content: testimonial.content,
      student_type: testimonial.student_type || ''
    });
    setEditingId(testimonial.id);
    setShowForm(true);
  };

  const handleToggleApproval = async (id) => {
    try {
      const response = await axios.patch(`${API}/testimonials/${id}/approve`, {}, getAuthHeaders());
      toast.success(response.data.is_approved ? 'Testimonial approved' : 'Testimonial hidden');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, getAuthHeaders());
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Testimonials</h1>
          <p className="text-muted-foreground">Manage student testimonials</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ name: '', content: '', student_type: '' }); }}>
          <Plus className="w-4 h-4 mr-2" /> Add Testimonial
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border border-border p-6 mb-6">
          <h2 className="font-semibold mb-4">{editingId ? 'Edit Testimonial' : 'New Testimonial'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                  placeholder="e.g., Sarah M."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.student_type}
                  onChange={(e) => setFormData({ ...formData, student_type: e.target.value })}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                >
                  <option value="">Select type...</option>
                  <option value="Parent">Parent</option>
                  <option value="Adult Student">Adult Student</option>
                  <option value="Young Performer">Young Performer</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Testimonial</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
                placeholder="What they said..."
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit">{editingId ? 'Update' : 'Add'} Testimonial</Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border border-border divide-y divide-border">
        {testimonials.length === 0 ? (
          <p className="p-6 text-center text-muted-foreground">No testimonials yet</p>
        ) : (
          testimonials.map((testimonial) => (
            <div key={testimonial.id} className="p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">{testimonial.name}</span>
                  {testimonial.student_type && (
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">{testimonial.student_type}</span>
                  )}
                  <span className={`text-xs px-2 py-1 rounded ${testimonial.is_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {testimonial.is_approved ? 'Visible' : 'Hidden'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{testimonial.content}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => handleToggleApproval(testimonial.id)} title={testimonial.is_approved ? 'Hide' : 'Show'}>
                  {testimonial.is_approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleEdit(testimonial)}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(testimonial.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
